(() => {
  if (window.ArcticTheme) return;

  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const dialogOpeners = new WeakMap();
  const dialogCloseJobs = new WeakMap();
  const disclosureJobs = new WeakMap();
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const getFocusable = (container) =>
    [...container.querySelectorAll(focusableSelector)].filter((element) => {
      const styles = window.getComputedStyle(element);
      return !element.hidden
        && !element.closest('[inert]')
        && element.getClientRects().length > 0
        && styles.display !== 'none'
        && styles.visibility !== 'hidden';
    });

  const getDialog = (dialogOrId) => {
    if (dialogOrId instanceof HTMLDialogElement) return dialogOrId;
    if (typeof dialogOrId === 'string') return document.getElementById(dialogOrId);
    return null;
  };

  const cssTimeToMilliseconds = (value) => {
    const time = Number.parseFloat(value);
    if (!Number.isFinite(time)) return 0;
    return value.trim().endsWith('ms') ? time : time * 1000;
  };

  const getTransitionMilliseconds = (element) => {
    if (!(element instanceof Element)) return 0;
    const styles = window.getComputedStyle(element);
    const durations = styles.transitionDuration.split(',').map(cssTimeToMilliseconds);
    const delays = styles.transitionDelay.split(',').map(cssTimeToMilliseconds);
    const count = Math.max(durations.length, delays.length);
    let longest = 0;

    for (let index = 0; index < count; index += 1) {
      longest = Math.max(
        longest,
        durations[index % durations.length] + delays[index % delays.length]
      );
    }

    return longest;
  };

  const setExpanded = (dialog, expanded) => {
    if (!dialog.id) return;
    document.querySelectorAll(`[data-dialog-open="${CSS.escape(dialog.id)}"]`).forEach((button) => {
      button.setAttribute('aria-expanded', String(expanded));
    });
  };

  const syncDialogLock = () => {
    document.body.classList.toggle(
      'has-open-dialog',
      Boolean(document.querySelector('dialog[data-theme-dialog][open]'))
    );
  };

  const cancelDialogClose = (dialog) => {
    const job = dialogCloseJobs.get(dialog);
    if (!job) return;
    window.clearTimeout(job.timer);
    dialogCloseJobs.delete(dialog);
  };

  const finishDialogClose = (dialog, restoreFocus = true) => {
    cancelDialogClose(dialog);
    const opener = dialogOpeners.get(dialog);

    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');

    dialog.removeAttribute('data-dialog-state');
    syncDialogLock();
    setExpanded(dialog, false);

    if (restoreFocus && opener instanceof HTMLElement && opener.isConnected) {
      opener.focus({ preventScroll: true });
    }
  };

  const openDialog = (dialogOrId, opener) => {
    const dialog = getDialog(dialogOrId);
    if (!dialog) return dialog;

    if (dialog.open && dialog.dataset.dialogState !== 'closing') return dialog;

    document.querySelectorAll('dialog[data-theme-dialog][open]').forEach((openDialogElement) => {
      if (openDialogElement !== dialog) finishDialogClose(openDialogElement, false);
    });

    dialogOpeners.set(dialog, opener || document.activeElement);

    if (dialog.open && dialog.dataset.dialogState === 'closing') {
      cancelDialogClose(dialog);
      dialog.dataset.dialogState = 'open';
    } else {
      dialog.dataset.dialogState = 'opening';

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    syncDialogLock();
    setExpanded(dialog, true);

    if (reducedMotionQuery.matches) {
      dialog.dataset.dialogState = 'open';
      const firstFocusable = getFocusable(dialog)[0];
      (firstFocusable || dialog).focus({ preventScroll: true });
      return dialog;
    }

    void dialog.offsetWidth;

    window.requestAnimationFrame(() => {
      if (
        !dialog.open
        || (dialog.dataset.dialogState !== 'opening' && dialog.dataset.dialogState !== 'open')
      ) return;
      if (dialog.dataset.dialogState === 'opening') dialog.dataset.dialogState = 'open';
      const firstFocusable = getFocusable(dialog)[0];
      (firstFocusable || dialog).focus({ preventScroll: true });
    });

    return dialog;
  };

  const closeDialog = (dialogOrId, restoreFocus = true) => {
    const dialog = getDialog(dialogOrId);
    if (!dialog || !dialog.open || dialog.dataset.dialogState === 'closing') return;

    setExpanded(dialog, false);

    if (reducedMotionQuery.matches) {
      finishDialogClose(dialog, restoreFocus);
      return;
    }

    dialog.dataset.dialogState = 'closing';
    const duration = Math.max(getTransitionMilliseconds(dialog), 1);
    const timer = window.setTimeout(
      () => finishDialogClose(dialog, restoreFocus),
      duration + 80
    );
    dialogCloseJobs.set(dialog, { timer });
  };

  const finishDisclosure = (details, shouldOpen) => {
    const job = disclosureJobs.get(details);
    if (job) window.clearTimeout(job.timer);
    disclosureJobs.delete(details);

    const summary = details.querySelector(':scope > summary');
    const region = details.querySelector(':scope > .motion-details__region');

    if (shouldOpen) {
      details.dataset.detailsState = 'open';
      summary?.setAttribute('aria-expanded', 'true');
      if (region) region.inert = false;
      return;
    }

    details.open = false;
    details.dataset.detailsState = 'closed';
    summary?.setAttribute('aria-expanded', 'false');
    if (region) region.inert = true;
  };

  const scheduleDisclosureFinish = (details, shouldOpen, motionTarget) => {
    const previousJob = disclosureJobs.get(details);
    if (previousJob) window.clearTimeout(previousJob.timer);

    if (reducedMotionQuery.matches) {
      finishDisclosure(details, shouldOpen);
      return;
    }

    const duration = Math.max(getTransitionMilliseconds(motionTarget), 1);
    const timer = window.setTimeout(
      () => finishDisclosure(details, shouldOpen),
      duration + 50
    );
    disclosureJobs.set(details, { timer, shouldOpen });
  };

  const toggleDisclosure = (details, shouldOpen) => {
    const summary = details.querySelector(':scope > summary');
    const region = details.querySelector(':scope > .motion-details__region');
    const popover = details.dataset.smoothDetails === 'popover';
    const motionTarget = popover
      ? details.querySelector(':scope > .site-header__submenu') || details
      : region;

    const previousJob = disclosureJobs.get(details);
    if (previousJob) window.clearTimeout(previousJob.timer);
    disclosureJobs.delete(details);

    if (shouldOpen) {
      if (!details.open) {
        details.dataset.detailsState = 'closed';
        details.open = true;
        void (motionTarget || details).offsetHeight;
      }
      if (region) region.inert = false;
      summary?.setAttribute('aria-expanded', 'true');
      details.dataset.detailsState = reducedMotionQuery.matches ? 'open' : 'opening';
      scheduleDisclosureFinish(details, true, motionTarget);
      return;
    }

    if (details.contains(document.activeElement) && document.activeElement !== summary) {
      summary?.focus({ preventScroll: true });
    }
    if (region) region.inert = true;
    summary?.setAttribute('aria-expanded', 'false');
    details.dataset.detailsState = reducedMotionQuery.matches ? 'closed' : 'closing';
    scheduleDisclosureFinish(details, false, motionTarget);
  };

  const initializeSmoothDetails = (scope = document) => {
    const disclosures = [];
    if (scope.matches?.('details[data-smooth-details]')) disclosures.push(scope);
    scope.querySelectorAll?.('details[data-smooth-details]').forEach((details) => {
      disclosures.push(details);
    });

    disclosures.forEach((details) => {
      if (details.dataset.detailsReady === 'true') return;
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      if (details.dataset.smoothDetails !== 'popover') {
        const region = document.createElement('div');
        const inner = document.createElement('div');
        region.className = 'motion-details__region';
        inner.className = 'motion-details__inner';

        while (summary.nextSibling) inner.append(summary.nextSibling);
        region.append(inner);
        details.append(region);
        region.inert = !details.open;
      }

      details.dataset.detailsReady = 'true';
      details.dataset.detailsState = details.open ? 'open' : 'closed';
      summary.setAttribute('aria-expanded', String(details.open));

      summary.addEventListener('click', (event) => {
        if (event.defaultPrevented) return;
        const interactiveChild = event.target.closest?.('a, button, input, select, textarea');
        if (interactiveChild && interactiveChild !== summary) return;

        event.preventDefault();
        const state = details.dataset.detailsState;
        const shouldOpen = state !== 'open' && state !== 'opening';
        toggleDisclosure(details, shouldOpen);
      });
    });
  };

  const announce = (message) => {
    const status = document.querySelector('[data-global-status]');
    if (!status || !message) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  };

  const trapDialogFocus = (event) => {
    if (event.key !== 'Tab') return;
    const dialog = event.currentTarget;
    const focusable = getFocusable(dialog);

    if (!focusable.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const initializeDialogs = (scope = document) => {
    scope.querySelectorAll('dialog[data-theme-dialog]').forEach((dialog) => {
      if (dialog.dataset.dialogReady === 'true') return;
      dialog.dataset.dialogReady = 'true';
      dialog.setAttribute('tabindex', '-1');

      dialog.addEventListener('keydown', trapDialogFocus);
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        const outside = event.clientX < bounds.left || event.clientX > bounds.right
          || event.clientY < bounds.top || event.clientY > bounds.bottom;
        if (outside) closeDialog(dialog);
      });
      dialog.addEventListener('close', () => {
        cancelDialogClose(dialog);
        dialog.removeAttribute('data-dialog-state');
        syncDialogLock();
        setExpanded(dialog, false);
      });
    });
  };

  const initializeAnnouncements = (scope = document) => {
    scope.querySelectorAll('[data-announcement]').forEach((announcement) => {
      if (announcement.dataset.announcementReady === 'true') return;
      announcement.dataset.announcementReady = 'true';

      const storageKey = `arctic-announcement-${announcement.dataset.announcementKey}`;
      if (!window.Shopify?.designMode) {
        try {
          if (window.localStorage.getItem(storageKey) === 'dismissed') {
            announcement.hidden = true;
          }
        } catch (_error) {
          // Storage can be unavailable in privacy-restricted browsing contexts.
        }
      }

      announcement.querySelector('[data-announcement-dismiss]')?.addEventListener('click', () => {
        announcement.hidden = true;
        if (!window.Shopify?.designMode) {
          try {
            window.localStorage.setItem(storageKey, 'dismissed');
          } catch (_error) {
            // Dismissal still works for the current page when storage is unavailable.
          }
        }
        updateHeaderHeight();
      });
    });
  };

  const initializeLocalization = (scope = document) => {
    scope.querySelectorAll('[data-localization-select]').forEach((select) => {
      if (select.dataset.localizationReady === 'true') return;
      select.dataset.localizationReady = 'true';
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (typeof form?.requestSubmit === 'function') form.requestSubmit();
        else form?.submit();
      });
    });
  };

  let revealObserver;
  const initializeReveals = (scope = document) => {
    const revealElements = scope.querySelectorAll('[data-reveal]');
    if (!revealElements.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const enabled = document.body.dataset.motionEnabled === 'true' && !reducedMotion;

    if (!enabled || !('IntersectionObserver' in window)) {
      revealElements.forEach((element) => element.classList.add('is-revealed'));
      return;
    }

    revealObserver ||= new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8%', threshold: 0.12 }
    );

    revealElements.forEach((element) => revealObserver.observe(element));
  };

  const updateHeaderState = () => {
    document.querySelectorAll('[data-site-header]').forEach((header) => {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
    });
  };

  const updateHeaderHeight = () => {
    const visibleAnnouncement = document.querySelector('[data-announcement]:not([hidden])');
    const header = document.querySelector('[data-site-header]');
    const announcementHeight = visibleAnnouncement?.getBoundingClientRect().height || 0;
    const headerHeight = header?.getBoundingClientRect().height || 0;
    root.style.setProperty('--announcement-height', `${Math.round(announcementHeight)}px`);
    if (headerHeight > 0) {
      root.style.setProperty(
        '--header-stack-height',
        `${Math.round(announcementHeight + headerHeight)}px`
      );
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(opener.dataset.dialogOpen, opener);
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('dialog'));
    }
  });

  const initialize = (scope = document) => {
    initializeSmoothDetails(scope);
    initializeDialogs(scope);
    initializeAnnouncements(scope);
    initializeLocalization(scope);
    initializeReveals(scope);
    updateHeaderState();
    updateHeaderHeight();
  };

  window.ArcticTheme = {
    announce,
    closeDialog,
    getFocusable,
    initialize,
    openDialog,
    updateHeaderHeight
  };

  initialize();
  window.addEventListener('scroll', updateHeaderState, { passive: true });
  window.addEventListener('resize', updateHeaderHeight, { passive: true });

  if ('ResizeObserver' in window) {
    const headerObserver = new ResizeObserver(updateHeaderHeight);
    document.querySelectorAll('[data-announcement], [data-site-header]').forEach((element) => {
      headerObserver.observe(element);
    });
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
